import { Component, Input, OnInit } from '@angular/core';
import { ICandidate } from '@shared/interfaces/candidate.interface';
import { IPayload } from '@shared/interfaces/payload.interface';
import { CandidatesService } from '@shared/services/candidates.service';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import jwtDecode from 'jwt-decode';
import { SidebarService } from '@shared/services/sidebar.service';
import { SessionStorage } from 'ngx-webstorage';
import { ISetting } from '@shared/interfaces/setting.interface';
import { PaginationCandidatesService } from '@shared/services/pagination/pagination.candidates.service';

@Component({
    selector: 'app-sidebar-candidate-details',
    templateUrl: './sidebar.candidate.details.component.html',
    standalone: false
})
export class SidebarCandidateDetailsComponent implements OnInit {
  @SessionStorage() public settings: ISetting;

  @Input() public candidate: ICandidate;

  public isLoading = Boolean(true);
  public isEditable = Boolean(false);
  public isButtonsDisabled = Boolean(false);
  public canPurgeData = Boolean(false);

  public openAnonymizeConfirmModal: Subject<void> = new Subject<void>();
  public openConfirmModal: Subject<void> = new Subject<void>();
  public openCandidateModal: Subject<any> = new Subject();
  public closeModal: Subject<void> = new Subject<void>();

  private anonymizeCandidateId: string | null = null;

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _logger: LoggerService,
    private readonly _candidates: CandidatesService,
    private readonly _toast: ToastService,
    private readonly _sidebar: SidebarService,
    private readonly _router: Router,
    private readonly _paginationCandidates: PaginationCandidatesService
  ) {
    this.afterSubmit = this.afterSubmit.bind(this);
    this.delete = this.delete.bind(this);
    this.anonymize = this.anonymize.bind(this);
  }

  ngOnInit(): void {
    this.getCandidate();
  }

  public afterSubmit() {
    this.getCandidate(true);
  }

  public anonymize() {
    if (!this.anonymizeCandidateId) {
      this._toast.error('No candidate selected for anonymization.');
      return;
    }
    this.isButtonsDisabled = true;
    const url = `POST  /candidates/${this.anonymizeCandidateId}/anonymize`;
    this._candidates.anonymize(this.anonymizeCandidateId).subscribe(
      (res: ICandidate) => {
        this._logger.info(this.constructorName, url, res);
        this._toast.success('Anonymize candidate succeeded');
        this.isButtonsDisabled = false;

        this._paginationCandidates.reloadPage(
          this._paginationCandidates.currentPage
        );

        this.closeModal.next();
        this._sidebar.closeSidebar();
        this._router.navigate(['/candidates']);
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.isButtonsDisabled = false;
      }
    );
  }

  public delete() {
    this.isButtonsDisabled = true;
    const url = `DELETE  /candidates/${this.candidate.id}`;
    this._candidates.delete(this.candidate.id).subscribe(
      (res: ICandidate) => {
        this._logger.info(this.constructorName, url, res);
        this._toast.success('Delete candidate succeeded');
        this.isButtonsDisabled = false;

        this.closeModal.next();
        this._sidebar.closeSidebar();
        this._candidates.refresh(this.candidate);
        this._router.navigate(['/candidates']);
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.isButtonsDisabled = false;
      }
    );
  }

  public getCandidate(isRefresh?: boolean) {
    const id = this.candidate?.id;
    const url = `GET /candidates/${id}`;
    this._candidates.find(id).subscribe(
      (res: ICandidate) => {
        this._logger.info(this.constructorName, url, res);

        this.candidate = res;

        const token = this._commonEnvironments.getToken();
        const payload: IPayload = jwtDecode(token);

        this.isEditable =
          payload.recruiter_id === this.candidate.added_by ||
          payload.roles.includes('root_recruiter');

        this.canPurgeData =
          payload.roles.includes('root_recruiter') ||
          payload.roles.includes('supervisor');

        if (isRefresh) {
          this._toast.success('Candidate refreshed');
          this._candidates.refresh(this.candidate);
        }

        this.isLoading = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.isLoading = false;
      }
    );
  }

  public openAnonymizeModal() {
    this.anonymizeCandidateId = this.candidate?.id || null;
    this.openAnonymizeConfirmModal.next();
  }

  public openDeleteModal() {
    this.openConfirmModal.next();
  }

  public openModal() {
    this.openCandidateModal.next(this.candidate);
  }
}
